import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { BlockedProductListEntry } from 'contracts/blocked-products';
import { useBlockedProductReasonTypeOptions } from 'hooks/api/options';
import { useUpdateBlockedProduct } from 'hooks/api/blocked-products';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';
import { ProductsTable } from 'pages/stock-inspection/shared-components';

import { formSchema, type FormSchema } from './form-schema';

export type UpdateProductReasonDrawerRef = {
  open: (product: BlockedProductListEntry) => void;
  close: () => void;
}

type OnSubmitPayload = Parameters<
  ReturnType<typeof useUpdateBlockedProduct>['updateProduct']
>[0];

type Props = {
  onSubmit: (data: OnSubmitPayload) => void;
  loading?: boolean;
}

export const UpdateProductReasonDrawer =
  React.forwardRef<UpdateProductReasonDrawerRef, Props>(({
                                                           onSubmit,
                                                           loading,
                                                         }, ref) => {
    const [product, setProduct] = React.useState<BlockedProductListEntry>();
    const [open, setOpen] = React.useState(false);

    const reasonOptions = useBlockedProductReasonTypeOptions();

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
    });

    const currentReason = useWatch({
      control: form.control,
      name: 'productReasonTypeId',
      defaultValue: `${product?.productReasonTypeId}`
    });

    const reasonChanged = React.useMemo(() => {
      return `${product?.productReasonTypeId}` !== currentReason;
    }, [currentReason, product?.productReasonTypeId]);

    const onFormSubmit = React.useCallback((formData: FormSchema) => {
      if (!product) return;

      onSubmit({
        productId: product.id,
        payload: {
          productReasonTypeId: +formData.productReasonTypeId,
        },
      });
    }, [onSubmit, product]);

    const onOpenChange = React.useCallback((open: boolean) => {
      setOpen(open);
      form.reset();
    }, [form, product]);

    const hydrateFormData = React.useCallback((product: BlockedProductListEntry) => {
      const { productReasonTypeId } = product;
      if (productReasonTypeId) {
        form.setValue('productReasonTypeId', productReasonTypeId.toString());
      }
    }, []);

    React.useImperativeHandle(ref, () => ({
      open: (product) => {
        onOpenChange(true);
        setProduct(product);
        hydrateFormData(product);
      },
      close: () => {
        onOpenChange(false);
        setProduct(undefined);
      },
    }), [onOpenChange, hydrateFormData]);

    return (
      <Drawer.Root open={open} onOpenChange={onOpenChange}>
        <Drawer.Content>
          <div className="max-w-[800px] w-full mx-auto">
            <Drawer.Header>
              <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
                <span>Alterar motivo</span>
                <span className={'font-extrabold text-gray-800 text-xl'}>
                {product?.materialDescription}
              </span>
              </Drawer.Title>
            </Drawer.Header>
            {product && (
              <ProductsTable products={[product]} className={'mb-6'} />
            )}
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onFormSubmit)}
                className="flex flex-col gap-4 pb-10"
              >
                <div className={'grid grid-cols-1 gap-4'}>
                  <FormField
                    control={form.control}
                    name={'productReasonTypeId'}
                    render={({ field }) => (
                      <ComboboxGs
                        form={form}
                        fieldName={field.name}
                        fieldValue={field.value}
                        content={reasonOptions.singleSelectOptions}
                        label={'Motivo'}
                        portal={false}
                      />
                    )}
                  />
                </div>
                <div className={'grid grid-cols-2 gap-4'}>
                  <Button.Root
                    type={'button'}
                    size={'sm'}
                    variant={'secondary'}
                    onClick={() => onOpenChange(false)}
                  >
                    Cancelar
                  </Button.Root>
                  <Button.Root
                    type={'submit'}
                    size={'sm'}
                    disabled={loading || !reasonChanged}
                  >
                    {loading ? <ActivityIndicator /> : 'Salvar'}
                  </Button.Root>
                </div>
              </form>
            </Form>
          </div>
        </Drawer.Content>
      </Drawer.Root>
    );
  });
UpdateProductReasonDrawer.displayName = 'UpdateProductReasonDrawer';